import React, { useEffect, useState, useCallback } from "react";
import styles from "./ViewAllAnnouncement.module.css";
import { FaBullhorn } from "react-icons/fa";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../../services/Api";
import BlobImage from "../../../../components/BlobImage/BlobImage";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

const TAB_KEYS = [
  { key: "ANNOUNCEMENTS", label: "announcements" },
  { key: "MANAGEMENT TALK", label: "managementTalk" },
  { key: "BIRTHDAY", label: "birthday" },
  { key: "WORK ANNIVERSARY", label: "workAnniversary" },
];

const ViewAllAnnouncement = () => {
  const { translations } = useSelector((state) => state.language);
  const location = useLocation();
  const defaultTab = location.state?.defaultTab || "ALL";
  const { ID } = location.state || {};
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const [activeTab, setActiveTab] = useState(
    defaultTab === "ALL" ? "ANNOUNCEMENTS" : defaultTab,
  );
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ---------- API FETCHERS ----------

  const fetchAnnouncement = useCallback(async () => {
    try {
      const response = await AxiosInstance.post("/school/listAnnouncement", {
        id: ID || "",
        school_id,
        search: "",
        page: "",
        limit: "",
      });
      return response.data?.data || [];
    } catch (error) {
      console.error("Announcement fetch error:", error);
      return [];
    }
  }, [school_id, ID]);

  const fetchManagementTalk = useCallback(async () => {
    try {
      const response = await AxiosInstance.post("/school/listManagementTalk", {
        id: ID || "",
        school_id,
        search: "",
      });
      return response.data?.data || [];
    } catch (error) {
      console.error("Management Talk fetch error:", error);
      return [];
    }
  }, [school_id, ID]);

  const fetchBirthdays = useCallback(async () => {
    try {
      const response = await AxiosInstance.post("/school/birthdays", {
        school_id: school_id,
        date: moment().format("YYYY-MM-DD"),
      });
      return response.data?.data || [];
    } catch (error) {
      console.error("Birthday fetch error:", error);
      return [];
    }
  }, [school_id]);

  const fetchAnniversary = useCallback(async () => {
    try {
      const response = await AxiosInstance.post("/school/anniversary", {
        school_id: school_id,
        date: moment().format("YYYY-MM-DD"),
      });
      return response.data?.data || [];
    } catch (error) {
      console.error("Anniversary fetch error:", error);
      return [];
    }
  }, [school_id]);

  // ---------- FETCH BASED ON ACTIVE TAB ----------

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      let result = [];

      switch (activeTab) {
        case "ANNOUNCEMENTS":
          result = await fetchAnnouncement();
          break;
        case "MANAGEMENT TALK":
          result = await fetchManagementTalk();
          break;
        case "BIRTHDAY":
          result = await fetchBirthdays();
          break;
        case "WORK ANNIVERSARY":
          result = await fetchAnniversary();
          break;
        default:
          result = [];
      }

      setData(result);
      setLoading(false);
    };

    fetchData();
  }, [
    activeTab,
    fetchAnnouncement,
    fetchManagementTalk,
    fetchBirthdays,
    fetchAnniversary,
  ]);

  // ---------- RENDER HELPERS ----------

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${IMAGE_BASE_URL}${path}`;
  };

  const renderAnnouncementItem = (item) => (
    <div key={item.id} className={styles.announcementRow}>
      <div className={styles.leftSection}>
        <div className={styles.iconWrapper}>
          <FaBullhorn />
        </div>
        <div>
          <span className={styles.itemName}>{item.subject}</span>
          <div className={styles.date}>
            {moment(item.created_at).format("DD-MMM-YYYY")}
          </div>
          <div>{item.content}</div>
        </div>
      </div>
    </div>
  );

  const renderManagementTalkItem = (item) => (
    <div key={item.id} className={styles.managementRow}>
      <div className={styles.managementImage}>
        {item.image ? (
          <BlobImage path={item.image} alt={item.name} />
        ) : (
          <div className={styles.placeholderImage} />
        )}
      </div>
      <div className={styles.managementInfo}>
        <span className={styles.managementTitle}>
          {item.name || item.designation || ""}
        </span>
        <span className={styles.managementContent}>{item.content}</span>
        <span className={styles.managementDate}>
          {moment(item.updated_at).format("DD-MMM-YYYY")}
        </span>
      </div>
    </div>
  );

  const renderBirthdayItem = (item, index) => (
    <div key={index} className={styles.personRow}>
      <div className={styles.avatarWrapper}>
        {item.photo ? (
          <BlobImage
            path={item.photo}
            alt={item.name}
            className={styles.avatarPhoto}
          />
        ) : (
          <div className={styles.avatarPlaceholder}>
            <svg viewBox="0 0 64 64" className={styles.avatarSvg}>
              <circle cx="32" cy="24" r="12" fill="currentColor" />
              <ellipse cx="32" cy="56" rx="20" ry="16" fill="currentColor" />
            </svg>
          </div>
        )}
      </div>
      <div className={styles.personInfo}>
        <span className={styles.personName}>{item.name}</span>
        {item.is_student === 1 && item.class && (
          <span className={styles.personRole}>{item.class}</span>
        )}
        <span className={styles.personDate}>
          {moment().format("DD-MMM-YYYY")}
        </span>
      </div>
    </div>
  );

  const renderAnniversaryItem = (item, index) => (
    <div key={index} className={styles.personRow}>
      <div className={styles.avatarWrapper}>
        <div className={styles.avatarPlaceholder}>
          <svg viewBox="0 0 64 64" className={styles.avatarSvg}>
            <circle cx="32" cy="24" r="12" fill="currentColor" />
            <ellipse cx="32" cy="56" rx="20" ry="16" fill="currentColor" />
          </svg>
        </div>
      </div>
      <div className={styles.personInfo}>
        <span className={styles.personName}>{item.name}</span>
        <span className={styles.personRole}>
          {translations.labels.teaching}
        </span>
        <span className={styles.personDate}>
          {moment().format("DD-MMM-YYYY")}
        </span>
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.loadingText}>{translations.labels.loading}</div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className={styles.emptyText}>
          {translations.labels.noRecordsToDisplay}
        </div>
      );
    }

    switch (activeTab) {
      case "ANNOUNCEMENTS":
        return data.map(renderAnnouncementItem);
      case "MANAGEMENT TALK":
        return data.map(renderManagementTalkItem);
      case "BIRTHDAY":
        return data.map(renderBirthdayItem);
      case "WORK ANNIVERSARY":
        return data.map(renderAnniversaryItem);
      default:
        return null;
    }
  };

  // ---------- DETERMINE VISIBLE TABS ----------

  const visibleTabs =
    defaultTab === "ALL"
      ? TAB_KEYS
      : TAB_KEYS.filter((tab) => tab.key === defaultTab);

  return (
    <div className={styles.container}>
      {/* Tabs */}
      <div className={styles.tabs}>
        {visibleTabs.map((tab) => (
          <div
            key={tab.key}
            className={`${styles.tab} ${
              activeTab === tab.key ? styles.active : ""
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label.toUpperCase()}
          </div>
        ))}
      </div>

      {/* Content */}
      <div className={styles.contentBox}>{renderContent()}</div>
    </div>
  );
};

export default ViewAllAnnouncement;
